import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  ACQUISITION_INPUT_DOCUMENT_FIGURES,
  acquisitionResultsOf,
  formatEuro,
  formatEuroExact,
  formatPercent,
  formatPercentInput,
  type AcquisitionCommercialSplit,
  type AcquisitionCostsPayer,
  type AcquisitionField,
  type AcquisitionInputs,
  type AcquisitionMode,
  type AcquisitionOptions,
  type AcquisitionPrefill,
  type AcquisitionResults,
  type AcquisitionSourceKey,
  type AcquisitionUnitRow,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Eyebrow,
  Icon,
  IconButton,
  Pill,
  Segmented,
  Select,
  useToast,
} from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { saveCalculation } from '../api';
import { activityDay, formatNumber } from '../format';
import type { CalculationVersion, CalculationsResponse } from '../types';
import {
  CALCULATION_FIELD_SPECS,
  CALCULATION_GROUPS,
  MODE_ITEMS,
  PAYER_ITEMS,
  SPLIT_ITEMS,
  UNIT_SUFFIX,
  autoNote,
  changedKeys,
  displayValue,
  draftFrom,
  effectiveInputs,
  emptyUnitDraft,
  inputsFromDraft,
  invalidOf,
  liveSource,
  optionsFromDraft,
  parseArea,
  parseRent,
  sourceLabel,
  startSources,
  unitsFromDraft,
  type CalculationChange,
  type CalculationDraft,
  type UnitDraft,
} from './calculation';
import { CardIntro } from './WizardShell';
import styles from './wizard.module.css';

const NBSP = ' ';

/** Whole euros („3.262.455 €"), Nico's `eur()`. */
export function euro(cents: number): string {
  return formatEuro(Math.round(cents / 100) * 100);
}

/** Euros with cents („2.029,98 €"), Nico's `eur2()`. */
export function euro2(cents: number): string {
  return formatEuroExact(cents);
}

/** „991,57 m²" — Nico's areas carry two decimals. */
export function area(sqm: number): string {
  return `${formatNumber(sqm, 2)}${NBSP}m²`;
}

/** „19,9 ×" */
export function factor(value: number): string {
  return `${formatNumber(value, 1)}${NBSP}×`;
}

export interface CalculationState {
  draft: CalculationDraft;
  setField: (field: AcquisitionField, text: string) => void;
  /** „Rechenweg"; switching carries the solved price / the current margin over like Nico's calculator. */
  setMode: (mode: AcquisitionMode) => void;
  setTargetMargin: (text: string) => void;
  setPayer: (payer: AcquisitionCostsPayer) => void;
  setCommercialSplit: (split: AcquisitionCommercialSplit) => void;
  setUnit: (key: string, patch: Partial<Omit<UnitDraft, 'key'>>) => void;
  addUnit: () => void;
  removeUnit: (key: string) => void;
  /** Replaces the unit list with the property's units (`prefill.units`). */
  loadPropertyUnits: () => void;
  /** The inputs computed and saved with (target mode: Kaufpreis = the solved max. Kaufpreis). */
  inputs: AcquisitionInputs;
  options: AcquisitionOptions;
  units: AcquisitionUnitRow[];
  results: AcquisitionResults | null;
  invalid: { fields: AcquisitionField[]; units: string[]; targetMargin: boolean };
  changed: CalculationChange[];
  /** At least one Gewerbe row in the unit list. */
  hasCommercial: boolean;
  prefill: AcquisitionPrefill;
  dirty: boolean;
  saving: boolean;
  /** `POST /properties/:id/calculations`; resolves false when nothing could be saved. */
  save: () => Promise<boolean>;
}

const EMPTY_PREFILL: AcquisitionPrefill = {
  landTransferTaxPct: 0,
  landTransferTaxSource: '',
  units: [],
  unitsSource: null,
};

/** The draft of Objekt anlegen 05, seeded from the current version and re-seeded when a new version arrives. */
export function useCalculationDraft(
  propertyId: string,
  calculations: CalculationsResponse | null,
  onSaved: (response: CalculationsResponse) => void,
): CalculationState {
  const current = calculations?.current ?? null;
  const prefill = calculations?.prefill ?? EMPTY_PREFILL;
  const [draft, setDraft] = useState<CalculationDraft>(() => draftFrom(current, prefill));
  const [seededFrom, setSeededFrom] = useState<string | null | undefined>(
    calculations === null ? undefined : (current?.id ?? null),
  );
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (calculations === null) return;
    const id = current?.id ?? null;
    if (id !== seededFrom) {
      setDraft(draftFrom(current, prefill));
      setSeededFrom(id);
    }
  }, [calculations, current, prefill, seededFrom]);

  const typed = useMemo(() => inputsFromDraft(draft.fields), [draft.fields]);
  const options = useMemo(() => optionsFromDraft(draft.options), [draft.options]);
  const units = useMemo(() => unitsFromDraft(draft.units), [draft.units]);
  const results = useMemo(() => acquisitionResultsOf(typed, units, options), [typed, units, options]);
  const inputs = useMemo(
    () => effectiveInputs(typed, options, results?.maxPurchasePriceCents),
    [typed, options, results],
  );
  const invalid = useMemo(() => invalidOf(draft), [draft]);
  const changed = useMemo(
    () => changedKeys(inputs, units, current, prefill, options),
    [inputs, units, current, prefill, options],
  );
  const hasCommercial = units.some((row) => !row.residential);
  const dirty =
    changed.length > 0 ||
    (current === null && (inputs.purchasePriceCents !== null || options.mode === 'target_margin'));

  const setField = useCallback((field: AcquisitionField, text: string) => {
    setDraft((state) => ({ ...state, fields: { ...state.fields, [field]: text } }));
  }, []);
  const setMode = useCallback(
    (mode: AcquisitionMode) => {
      setDraft((state) => {
        if (state.options.mode === mode) return state;
        // Nico's switch: to the target mode with the current margin, back with the solved price
        if (mode === 'target_margin') {
          const margin =
            state.options.targetMargin.trim() === '' && results !== null
              ? formatPercentInput(Math.round(results.marginOnTotalInvestmentPct * 10) / 10)
              : state.options.targetMargin;
          return { ...state, options: { ...state.options, mode, targetMargin: margin } };
        }
        const solved = results?.maxPurchasePriceCents ?? null;
        return {
          ...state,
          fields:
            solved === null
              ? state.fields
              : { ...state.fields, purchasePriceCents: displayValue('purchasePriceCents', solved) },
          options: { ...state.options, mode },
        };
      });
    },
    [results],
  );
  const setTargetMargin = useCallback((text: string) => {
    setDraft((state) => ({ ...state, options: { ...state.options, targetMargin: text } }));
  }, []);
  const setPayer = useCallback((payer: AcquisitionCostsPayer) => {
    setDraft((state) => ({ ...state, options: { ...state.options, acquisitionCostsPayer: payer } }));
  }, []);
  const setCommercialSplit = useCallback((split: AcquisitionCommercialSplit) => {
    setDraft((state) => ({ ...state, options: { ...state.options, commercialSplit: split } }));
  }, []);
  const setUnit = useCallback((key: string, patch: Partial<Omit<UnitDraft, 'key'>>) => {
    setDraft((state) => ({
      ...state,
      units: state.units.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    }));
  }, []);
  const addUnit = useCallback(() => {
    setDraft((state) => ({ ...state, units: [...state.units, emptyUnitDraft(state.units.length)] }));
  }, []);
  const removeUnit = useCallback((key: string) => {
    setDraft((state) => ({ ...state, units: state.units.filter((row) => row.key !== key) }));
  }, []);
  const loadPropertyUnits = useCallback(() => {
    setDraft((state) => ({ ...state, units: draftFrom(null, prefill).units }));
  }, [prefill]);

  const save = useCallback(async () => {
    if (invalid.fields.length > 0 || invalid.units.length > 0 || invalid.targetMargin) {
      toast({ message: 'Bitte prüfen Sie die markierten Werte.' });
      return false;
    }
    setSaving(true);
    try {
      const response = await saveCalculation(propertyId, {
        ...inputs,
        ...options,
        units,
        note: autoNote(changed, current),
        fieldSources: startSources(inputs, units, current, prefill, changed),
      });
      onSaved(response);
      toast({ message: `Version ${response.current?.label ?? ''} gespeichert.`.replace('  ', ' ') });
      return true;
    } catch (error) {
      toast({ message: errorMessage(error) });
      return false;
    } finally {
      setSaving(false);
    }
  }, [changed, current, inputs, invalid, onSaved, options, prefill, propertyId, toast, units]);

  return {
    draft,
    setField,
    setMode,
    setTargetMargin,
    setPayer,
    setCommercialSplit,
    setUnit,
    addUnit,
    removeUnit,
    loadPropertyUnits,
    inputs,
    options,
    units,
    results,
    invalid,
    changed,
    hasCommercial,
    prefill,
    dirty,
    saving,
    save,
  };
}

function SourceTag({ source }: { source: { source: string; manual: boolean } | null }) {
  if (source === null) return null;
  return source.manual ? (
    <Pill variant="accent">manuell</Pill>
  ) : (
    <span className={styles.sourceText} title={source.source}>
      {source.source}
    </span>
  );
}

export interface CalculationMainProps {
  state: CalculationState;
  current: CalculationVersion | null;
  canEdit: boolean;
}

/** Objekt anlegen 05 in Nico's model: his three input panels, the unit list and the monthly figures per unit. */
export function CalculationMain({ state, current, canEdit }: CalculationMainProps) {
  const [showSources, setShowSources] = useState(false);
  const target = state.options.mode === 'target_margin';
  const sourceOf = (key: AcquisitionSourceKey) =>
    liveSource(
      key,
      key === 'units' ? state.units.length > 0 : state.inputs[key] !== null,
      state.changed,
      current,
      state.prefill,
      state.options.mode,
    );

  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-calculation">
        <CardIntro
          title="Ankaufskalkulation"
          text="Vorbefüllt aus den Unterlagen. Werte, die Sie ändern, werden als manuell markiert und bleiben im Verlauf nachvollziehbar."
          end={
            <div className={styles.assignActions}>
              <button
                type="button"
                className={styles.linkButton}
                aria-expanded={showSources}
                onClick={() => setShowSources((value) => !value)}
              >
                {showSources ? 'Quellen ausblenden' : 'Alle Quellen anzeigen'}
              </button>
              {canEdit ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.smallButton)}
                  disabled={!state.dirty || state.saving}
                  aria-busy={state.saving || undefined}
                  onClick={() => void state.save()}
                >
                  Version speichern
                </button>
              ) : null}
            </div>
          }
        />
        {current?.legacy && current.legacyNote ? (
          <Banner tone="info" className={styles.calcBanner}>
            {`Version ${current.label} ${current.legacyNote}: Erwerbsnebenkosten stehen als Notar / Grundbuch, Wohnfläche und Ist-Miete als eine Zeile „Gesamtobjekt“. Mit der nächsten Version rechnen Sie im Modell von Nico Kremer.`}
          </Banner>
        ) : null}
        {showSources ? (
          <div className={styles.sourcesList} aria-label="Quellen">
            {[...CALCULATION_FIELD_SPECS.map((spec) => spec.field), 'units' as const].map((key) => {
              const source = sourceOf(key);
              return (
                <div key={key} className={styles.sourcesRow}>
                  <span>{sourceLabel(key)}</span>
                  <span className={styles.sourceText}>{source ? source.source : 'noch nicht erfasst'}</span>
                </div>
              );
            })}
            {(current?.documentFigures ?? [])
              .filter((figure) => !ACQUISITION_INPUT_DOCUMENT_FIGURES.includes(figure.key))
              .map((figure) => (
                <div key={figure.key} className={styles.sourcesRow}>
                  <span>
                    {figure.key === 'livingAreaTotalSqm'
                      ? `Wohnfläche gesamt · ${formatNumber(figure.value, 1)}${NBSP}m²`
                      : figure.key === 'actualRentPaCents'
                        ? `Ist-Miete p. a. · ${euro(figure.value)}`
                        : `Erhaltungsrücklage · ${euro(figure.value)}`}
                  </span>
                  <span className={styles.sourceText}>{figure.source}</span>
                </div>
              ))}
          </div>
        ) : null}
        <div className={styles.calcOption}>
          <span className={styles.calcOptionLabel}>Rechenweg</span>
          <Segmented
            label="Rechenweg"
            className={styles.calcSegmented}
            value={state.options.mode}
            items={MODE_ITEMS.map((item) => ({ ...item, disabled: !canEdit }))}
            onChange={state.setMode}
          />
        </div>
        <div className={styles.calcGroups}>
          {CALCULATION_GROUPS.map((group, groupIndex) => (
            <div key={group.id}>
              <div className={clsx(styles.calcGroup, groupIndex === 0 && styles.calcGroupFirst)}>
                {group.label}
              </div>
              {group.id === 'purchase' && target ? (
                <div className={styles.calcRow}>
                  <label htmlFor="calc-targetMarginPct" className={styles.calcLabel}>
                    Zielmarge % auf GIK
                  </label>
                  <span
                    className={clsx(
                      styles.calcInputWrap,
                      state.invalid.targetMargin && styles.calcInputWrapInvalid,
                    )}
                  >
                    <input
                      id="calc-targetMarginPct"
                      className={styles.calcInput}
                      inputMode="decimal"
                      value={state.draft.options.targetMargin}
                      readOnly={!canEdit}
                      aria-invalid={state.invalid.targetMargin || undefined}
                      onChange={(event) => state.setTargetMargin(event.target.value)}
                    />
                    <span className={styles.calcUnit} aria-hidden="true">
                      %
                    </span>
                  </span>
                  <span className={styles.calcSource} />
                </div>
              ) : null}
              {group.fields.map((spec) => {
                const inputId = `calc-${spec.field}`;
                const solved = target && spec.field === 'purchasePriceCents';
                const invalid = state.invalid.fields.includes(spec.field);
                return (
                  <div key={spec.field} className={styles.calcRow}>
                    <label htmlFor={inputId} className={styles.calcLabel}>
                      {solved ? 'max. Kaufpreis' : spec.label}
                    </label>
                    <span
                      className={clsx(
                        styles.calcInputWrap,
                        invalid && styles.calcInputWrapInvalid,
                        solved && styles.calcInputWrapReadOnly,
                      )}
                    >
                      <input
                        id={inputId}
                        className={styles.calcInput}
                        inputMode={spec.unit === 'months' ? 'numeric' : 'decimal'}
                        value={
                          solved
                            ? displayValue('purchasePriceCents', state.inputs.purchasePriceCents)
                            : state.draft.fields[spec.field]
                        }
                        readOnly={!canEdit || solved}
                        aria-invalid={invalid || undefined}
                        onChange={(event) => state.setField(spec.field, event.target.value)}
                      />
                      <span className={styles.calcUnit} aria-hidden="true">
                        {UNIT_SUFFIX[spec.unit]}
                      </span>
                    </span>
                    <span className={styles.calcSource}>
                      <SourceTag source={sourceOf(spec.field)} />
                    </span>
                  </div>
                );
              })}
              {group.id === 'purchase' ? (
                <>
                  <div className={styles.calcRow}>
                    <span className={styles.calcLabel}>Kaufnebenkosten zahlt</span>
                    <Segmented
                      label="Kaufnebenkosten zahlt"
                      className={styles.calcSegmented}
                      value={state.options.acquisitionCostsPayer}
                      items={PAYER_ITEMS.map((item) => ({ ...item, disabled: !canEdit }))}
                      onChange={state.setPayer}
                    />
                    <span className={styles.calcSource} />
                  </div>
                  <p className={styles.calcNote}>
                    Verkäufer zahlt: Nebenkosten werden aus dem Kaufpreis herausgerechnet (KP ÷ (1 − NK %)).
                  </p>
                </>
              ) : null}
              {group.id === 'investor' ? (
                <p className={styles.calcNote}>
                  Subvention greift automatisch nur bei Wohnungen mit negativem Cashflow (Netto-Zuzahlung &gt;
                  0). Einmalbetrag = IST-Miete × Subv.-% × Monate, käuferfinanziert im VK.
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </section>
      <UnitListCard state={state} canEdit={canEdit} source={sourceOf('units')} />
      <UnitResultsCard results={state.results} />
    </>
  );
}

function UnitListCard({
  state,
  canEdit,
  source,
}: {
  state: CalculationState;
  canEdit: boolean;
  source: { source: string; manual: boolean } | null;
}) {
  const hasPropertyUnits = state.prefill.units.length > 0;
  return (
    <section className={styles.card} aria-labelledby="wizard-rent-roll">
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <h2 id="wizard-rent-roll" className={styles.cardTitle}>
            IST-Mietaufstellung
          </h2>
        </div>
        <div className={styles.assignActions}>
          <SourceTag source={source} />
          {canEdit && hasPropertyUnits ? (
            <button type="button" className={styles.linkButton} onClick={state.loadPropertyUnits}>
              Einheiten des Objekts übernehmen
            </button>
          ) : null}
        </div>
      </div>
      <div className={styles.rentRoll}>
        <div className={clsx(styles.rentRollRow, styles.rentRollHead)} aria-hidden="true">
          <span>Lage</span>
          <span>Einheit</span>
          <span className={styles.rentRollNum}>m²</span>
          <span className={styles.rentRollNum}>Kaltmiete/M</span>
          <span className={styles.rentRollNum}>€/m²</span>
          <span>Art</span>
          <span />
        </div>
        {state.draft.units.length === 0 ? (
          <div className={styles.rentRollEmpty}>
            Noch keine Einheiten erfasst. Fügen Sie die Einheiten mit Fläche und Kaltmiete hinzu.
          </div>
        ) : (
          state.draft.units.map((row, index) => {
            const areaValue = parseArea(row.area);
            const rentValue = parseRent(row.rent);
            const perSqm =
              typeof areaValue === 'number' && areaValue > 0 && typeof rentValue === 'number'
                ? formatEuroExact(Math.round(rentValue / areaValue))
                : '—';
            const label = row.number.trim() || `Zeile ${index + 1}`;
            const numberInvalid = row.number.trim() === '';
            return (
              <div key={row.key} className={styles.rentRollRow}>
                <span className={styles.calcInputWrap}>
                  <input
                    className={clsx(styles.calcInput, styles.calcInputText)}
                    aria-label={`Lage ${label}`}
                    value={row.position}
                    readOnly={!canEdit}
                    maxLength={80}
                    onChange={(event) => state.setUnit(row.key, { position: event.target.value })}
                  />
                </span>
                <span className={clsx(styles.calcInputWrap, numberInvalid && styles.calcInputWrapInvalid)}>
                  <input
                    className={clsx(styles.calcInput, styles.calcInputText)}
                    aria-label={`Einheit ${index + 1}`}
                    value={row.number}
                    readOnly={!canEdit}
                    maxLength={40}
                    aria-invalid={numberInvalid || undefined}
                    onChange={(event) => state.setUnit(row.key, { number: event.target.value })}
                  />
                </span>
                <span
                  className={clsx(
                    styles.calcInputWrap,
                    areaValue === undefined && styles.calcInputWrapInvalid,
                  )}
                >
                  <input
                    className={styles.calcInput}
                    aria-label={`Fläche ${label}`}
                    inputMode="decimal"
                    value={row.area}
                    readOnly={!canEdit}
                    aria-invalid={areaValue === undefined || undefined}
                    onChange={(event) => state.setUnit(row.key, { area: event.target.value })}
                  />
                </span>
                <span
                  className={clsx(
                    styles.calcInputWrap,
                    rentValue === undefined && styles.calcInputWrapInvalid,
                  )}
                >
                  <input
                    className={styles.calcInput}
                    aria-label={`Kaltmiete ${label}`}
                    inputMode="decimal"
                    value={row.rent}
                    readOnly={!canEdit}
                    aria-invalid={rentValue === undefined || undefined}
                    onChange={(event) => state.setUnit(row.key, { rent: event.target.value })}
                  />
                </span>
                <span className={styles.rentRollNum}>{perSqm}</span>
                <Select
                  label={`Art ${label}`}
                  labelHidden
                  size="compact"
                  readOnly={!canEdit}
                  value={row.residential ? 'residential' : 'commercial'}
                  options={[
                    { value: 'residential', label: 'Wohnen' },
                    { value: 'commercial', label: 'Gewerbe' },
                  ]}
                  onChange={(event) =>
                    state.setUnit(row.key, { residential: event.target.value === 'residential' })
                  }
                />
                {canEdit ? (
                  <IconButton
                    icon={Trash2}
                    variant="ghost"
                    size={28}
                    label={`${label} entfernen`}
                    onClick={() => state.removeUnit(row.key)}
                  />
                ) : (
                  <span />
                )}
              </div>
            );
          })
        )}
      </div>
      {canEdit ? (
        <Button
          variant="secondary"
          size="small"
          className={styles.rentRollAdd}
          icon={<Icon icon={Plus} size={16} />}
          onClick={state.addUnit}
        >
          Einheit hinzufügen
        </Button>
      ) : null}
      {state.hasCommercial ? (
        <div className={styles.calcOption}>
          <span className={styles.calcOptionLabel}>Gewerbeanteil nach</span>
          <Segmented
            label="Gewerbeanteil nach"
            className={styles.calcSegmented}
            value={state.options.commercialSplit}
            items={SPLIT_ITEMS.map((item) => ({ ...item, disabled: !canEdit }))}
            onChange={state.setCommercialSplit}
          />
        </div>
      ) : null}
    </section>
  );
}

/** Nico's „Eigenbelastung je Einheit (Monatswerte)" with the sum row. */
function UnitResultsCard({ results }: { results: AcquisitionResults | null }) {
  const signClass = (cents: number) => (cents > 0 ? styles.amountNegative : styles.amountPositive);
  return (
    <section className={styles.card} aria-labelledby="wizard-unit-results">
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <h2 id="wizard-unit-results" className={styles.cardTitle}>
            Eigenbelastung je Einheit (Monatswerte)
          </h2>
        </div>
      </div>
      {results === null ? (
        <div className={styles.rentRollEmpty}>
          Die Werte je Einheit erscheinen, sobald Kaufpreis, Ziel-Verkaufspreis und Einheiten erfasst sind.
        </div>
      ) : (
        <div className={styles.unitResults} role="table" aria-label="Eigenbelastung je Einheit">
          <div role="row" className={clsx(styles.unitResultsRow, styles.rentRollHead)}>
            <span role="columnheader">Einheit</span>
            <span role="columnheader" className={styles.rentRollNum}>
              VKP
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Rate/M
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Steuervorteil
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Netto ohne Subv.
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Subvention
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Einmalbetrag Subv.
            </span>
            <span role="columnheader" className={styles.rentRollNum}>
              Netto inkl. Subv.
            </span>
          </div>
          {results.units.map((row, index) => (
            <div role="row" key={`${row.number}-${index}`} className={styles.unitResultsRow}>
              <span role="cell" className={styles.unitResultsName}>
                <strong>{row.number}</strong>
                {row.position ? <span className={styles.sourceText}>{row.position}</span> : null}
              </span>
              <span role="cell" className={styles.rentRollNum}>
                {euro(row.salePriceCents)}
              </span>
              <span role="cell" className={styles.rentRollNum}>
                {euro2(row.installmentCents)}
              </span>
              <span role="cell" className={styles.rentRollNum}>
                {euro2(row.taxBenefitCents)}
              </span>
              <span role="cell" className={clsx(styles.rentRollNum, signClass(row.netWithoutSubsidyCents))}>
                {euro2(row.netWithoutSubsidyCents)}
              </span>
              <span role="cell" className={styles.rentRollNum}>
                {row.subsidyActive ? 'aktiv' : 'nein'}
              </span>
              <span role="cell" className={styles.rentRollNum}>
                {row.subsidyOneOffCents > 0 ? euro(row.subsidyOneOffCents) : '—'}
              </span>
              <span role="cell" className={clsx(styles.rentRollNum, signClass(row.netWithSubsidyCents))}>
                <strong>{euro2(row.netWithSubsidyCents)}</strong>
              </span>
            </div>
          ))}
          <div role="row" className={clsx(styles.unitResultsRow, styles.unitResultsSum)}>
            <span role="cell">
              <strong>Summe</strong>
            </span>
            <span role="cell" className={styles.rentRollNum}>
              <strong>{euro(results.units.reduce((sum, row) => sum + row.salePriceCents, 0))}</strong>
            </span>
            <span role="cell" />
            <span role="cell" />
            <span role="cell" />
            <span role="cell" />
            <span role="cell" className={styles.rentRollNum}>
              <strong>{euro(results.rentSubsidyTotalCents)}</strong>
            </span>
            <span role="cell" />
          </div>
        </div>
      )}
      <p className={styles.calcNote}>
        Netto &lt; 0 = Überschuss für den Käufer (grün) · Netto &gt; 0 = monatliche Zuzahlung (rot).
        Einmalbeträge fließen zusätzlich in den Verkaufspreis (käuferfinanziert).
      </p>
    </section>
  );
}

/** Nico's „Deal-Ergebnis" rows, shared by the wizard sidebar and the property record card. */
export function resultRows(
  results: AcquisitionResults,
  salesCommissionPct: number | null,
): { label: string; value: string; note?: string | undefined; strong?: boolean | undefined }[] {
  return [
    { label: 'Gesamtfläche', value: area(results.totalAreaSqm) },
    {
      label: 'Miete/Monat',
      value: euro(results.rentMonthlyCents),
      note: `${euro(results.rentAnnualCents)} p. a.`,
    },
    { label: 'Käufer-Maklercourtage', value: euro(results.brokerCommissionCents) },
    { label: 'Grunderwerbsteuer', value: euro(results.landTransferTaxCents) },
    { label: 'Notar / Grundbuch', value: euro(results.notaryCents) },
    { label: 'Einstand', value: euro(results.acquisitionCostCents) },
    { label: 'Gesamtkosten (GIK)', value: euro(results.totalInvestmentCents), strong: true },
    ...(results.commercialSplitBasis === null
      ? []
      : [
          {
            label: `− Gewerbeanteil (${formatPercent(results.commercialSharePct, 1)} nach ${
              results.commercialSplitBasis === 'rent' ? 'Miete' : 'Fläche'
            })`,
            value: euro(results.commercialShareCents),
            note: 'nicht im VK',
          },
          { label: 'GIK Wohnen', value: euro(results.gikResidentialCents), strong: true },
        ]),
    {
      label: 'Verkaufspreis (Basis)',
      value: euro(results.saleBaseCents),
      note: `${euro(results.salePricePerSqmCents)}/m² × ${area(results.totalAreaSqm)}`,
    },
    {
      label: 'Mietsubventionen',
      value: euro(results.rentSubsidyTotalCents),
      note: `einmalig, käuferfinanziert → VK inkl.: ${euro(results.saleInclSubsidyCents)}`,
    },
    {
      label: '− Vertriebsprovision',
      value: euro(results.salesCommissionCents),
      note: `${formatPercent(salesCommissionPct ?? 0, 2)} auf VK-Basis`,
    },
    {
      label: 'Abgabepreis',
      value: euro(results.handoverPriceCents),
      note: 'VK-Basis − Provision',
      strong: true,
    },
    {
      label: 'Faktoren',
      value: `${factor(results.purchaseFactor)} / ${factor(results.saleFactor)}`,
      note: 'Einkauf / Verkauf (× IST-Jahresmiete)',
    },
    {
      label: 'Bruttorendite Einkauf',
      value: formatPercent(results.grossYieldPct, 2),
      note: 'IST-Miete / Kaufpreis',
    },
    { label: 'VK je m²', value: euro(results.salePricePerSqmCents) },
  ];
}

/** The dark result card: Nico's „Marge" hero and his summary rows. */
export function ResultCard({
  results,
  salesCommissionPct,
  headingId,
}: {
  results: AcquisitionResults | null;
  salesCommissionPct: number | null;
  headingId: string;
}) {
  return (
    <section className={clsx(styles.card, styles.cardDark)} aria-labelledby={headingId}>
      <Eyebrow id={headingId} className={styles.darkEyebrow}>
        Deal-Ergebnis
      </Eyebrow>
      {results === null ? (
        <div className={styles.resultNote}>
          Das Ergebnis erscheint, sobald Kaufpreis, Ziel-Verkaufspreis und Einheiten erfasst sind.
        </div>
      ) : (
        <>
          <div className={styles.resultRows}>
            {results.maxPurchasePriceCents === null ? (
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>Marge</span>
                <span
                  className={clsx(
                    styles.resultValue,
                    styles.resultValueLarge,
                    results.marginCents < 0 && styles.resultValueNegative,
                  )}
                >
                  {euro(results.marginCents)}
                </span>
              </div>
            ) : (
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>max. Kaufpreis</span>
                <span className={clsx(styles.resultValue, styles.resultValueLarge)}>
                  {euro(results.maxPurchasePriceCents)}
                </span>
              </div>
            )}
          </div>
          <div className={styles.resultNote}>
            {results.maxPurchasePriceCents === null
              ? `${formatPercent(results.marginOnTotalInvestmentPct, 1)} auf GIK`
              : `Marge ${euro(results.marginCents)} · ${formatPercent(results.marginOnTotalInvestmentPct, 1)} auf GIK`}
          </div>
          <div className={styles.resultRows}>
            {resultRows(results, salesCommissionPct).map((row) => (
              <div key={row.label} className={styles.resultRowStacked}>
                <div className={styles.resultRowLine}>
                  <span className={styles.resultLabel}>{row.label}</span>
                  <span className={clsx(styles.resultValue, row.strong && styles.resultValueStrong)}>
                    {row.value}
                  </span>
                </div>
                {row.note ? <div className={styles.resultSub}>{row.note}</div> : null}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export interface CalculationSidebarProps {
  state: CalculationState;
  versions: CalculationVersion[];
}

/** Objekt anlegen 05 sidebar: Nico's „Deal-Ergebnis" (live) and the version list. */
export function CalculationSidebar({ state, versions }: CalculationSidebarProps) {
  return (
    <>
      <ResultCard
        results={state.results}
        salesCommissionPct={state.inputs.salesCommissionPct}
        headingId="wizard-results"
      />
      <section className={styles.card} aria-labelledby="wizard-versions">
        <Eyebrow id="wizard-versions">Versionen</Eyebrow>
        <div className={styles.versions}>
          {versions.length === 0 ? (
            <div className={styles.versionRow}>
              <span className={styles.versionMeta}>Noch keine Version gespeichert.</span>
            </div>
          ) : (
            versions.map((version) => (
              <div key={version.id} className={styles.versionRow}>
                <span className={clsx(styles.versionBadge, version.current && styles.versionBadgeCurrent)}>
                  {version.label}
                </span>
                <span className={styles.versionText}>
                  <span className={styles.versionSummary}>{version.summary}</span>
                  <span className={styles.versionMeta}>
                    {version.legacyNote
                      ? `${activityDay(version.createdAt)} · ${version.legacyNote}`
                      : activityDay(version.createdAt)}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}
